import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { 
  FaBookMedical, 
  FaStar, 
  FaDownload, 
  FaShoppingCart, 
  FaEye, 
  FaBookReader, 
  FaFilePdf,
  FaUserEdit,
  FaLanguage,
  FaMedkit,
  FaAppleAlt,
  FaHeartbeat,
  FaFire,
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaArrowLeft,
  FaBookmark,
  FaShareAlt,
  FaPrint,
  FaHeart,
  FaSearch,
  FaExternalLinkAlt,
  FaPlay,
  FaAndroid,
  FaMobileAlt,
  FaGlobe,
  FaFileAlt,
  FaEyeDropper,
  FaArrowRight,
  FaBolt,
  FaBookOpen,
  FaLightbulb,
  FaGraduationCap,
  FaVideo,
  FaHeadphones,
  FaLaptop,
  FaRocket,
  FaCloudDownloadAlt,
  FaBook,
  FaMobile,
  FaTabletAlt,
  FaSpinner,
  FaTimesCircle
} from 'react-icons/fa';
import { 
  GiHealthPotion,
  GiBookshelf,
  GiOpenBook,
  GiScrollQuill
} from 'react-icons/gi';
import { motion, AnimatePresence } from 'framer-motion';
import { SiGoogleplay } from 'react-icons/si';

// Firebase imports
import { database } from '../firebase';
import { ref as dbRef, onValue } from 'firebase/database';

const Ebook = () => {
  const [selectedBook, setSelectedBook] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [savedBooks, setSavedBooks] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  const slideIn = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  // No filter options - showing all books

  // Platform options
  const platforms = [
    { id: 'playstore', label: 'Play Store', icon: <FaAndroid />, color: 'from-green-500 to-emerald-500' },
    { id: 'pdf', label: 'PDF Download', icon: <FaFilePdf />, color: 'from-red-500 to-pink-500' }
  ];

  // Fetch e-books from Firebase
  useEffect(() => {
    const ebooksRef = dbRef(database, 'Dr/eBooks');
    
    const unsubscribe = onValue(ebooksRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const ebooksArray = [];
          
          // Convert Firebase object to array
          Object.keys(data).forEach((key) => {
            if (key !== 'counter' && data[key]) {
              const ebook = data[key];
              
              // Transform Firebase data to match UI format
              const transformedEbook = {
                id: parseInt(ebook.ebookId) || parseInt(key),
                title: ebook.bookName || 'Untitled',
                author: ebook.authorName || 'Unknown Author',
                description: ebook.description || 'No description available',
                category: 'health', // You can add category field in admin form
                price: ebook.bookPrice || 0,
                discountPrice: ebook.discountedPrice || ebook.bookPrice || 0,
                discountPercent: ebook.discountType === 'percentage' ? ebook.discountValue : 
                                 Math.round(((ebook.bookPrice - ebook.discountedPrice) / ebook.bookPrice) * 100) || 0,
                rating: 4.5, // You can add rating system later
                reviews: 0, // You can add review system later
                pages: ebook.totalPages || 0,
                format: 'PDF',
                language: ebook.language || 'English',
                isFree: ebook.bookType === 'Free',
                isFeatured: ebook.status === 'Active',
                isNew: isNewBook(ebook.createdAt),
                icon: <FaBook />,
                color: 'from-indigo-500 to-purple-500',
                bgColor: 'bg-gradient-to-r from-indigo-50 to-purple-50',
                borderColor: 'border-indigo-200',
                buttonColor: 'from-indigo-500 to-purple-500',
                image: ebook.coverUrl || 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                playstoreLink: ebook.playStoreLink || '#',
                webLink: ebook.websiteLink || '#',
                pdfUrl: ebook.pdfUrls?.english || ebook.pdfUrls?.marathi || ebook.pdfUrls?.hindi || ebook.pdfUrls?.other || '#',
                audioLink: '#',
                previewPages: 20,
                platforms: getPlatforms(ebook),
                details: {
                  overview: ebook.description || 'No overview available',
                  chapters: ebook.tableOfContents?.map((chapter, index) => ({
                    title: chapter,
                    pages: Math.floor(ebook.totalPages / (ebook.tableOfContents.length || 1)),
                    duration: '45 mins'
                  })) || [
                    { title: 'Introduction', pages: 20, duration: '30 mins' },
                    { title: 'Main Content', pages: ebook.totalPages - 40 || 100, duration: '2 hours' },
                    { title: 'Conclusion', pages: 20, duration: '30 mins' }
                  ],
                  features: [
                    'Comprehensive content',
                    'Easy to understand',
                    'Downloadable PDF',
                    'Mobile-friendly',
                    'Expert authored'
                  ],
                  targetAudience: [
                    'Health enthusiasts',
                    'Patients',
                    'Caregivers',
                    'Medical students'
                  ],
                  benefits: [
                    'Better health knowledge',
                    'Improved wellness',
                    'Disease prevention',
                    'Quality of life improvement'
                  ]
                }
              };
              
              // Only add active ebooks
              if (ebook.isActive !== false) {
                ebooksArray.push(transformedEbook);
              }
            }
          });
          
          setEbooks(ebooksArray);
          setLoading(false);
        } else {
          setEbooks([]);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching ebooks:', err);
        setError('Failed to load e-books');
        setLoading(false);
      }
    }, (error) => {
      console.error('Firebase error:', error);
      setError('Failed to connect to database');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Helper function to check if book is new (within last 30 days)
  const isNewBook = (createdAt) => {
    if (!createdAt) return false;
    const bookDate = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - bookDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
  };

  // Helper function to get available platforms
  const getPlatforms = (ebook) => {
    const platforms = [];
    if (ebook.playStoreLink) platforms.push('playstore');
    if (ebook.pdfUrls && Object.keys(ebook.pdfUrls).length > 0) platforms.push('pdf');
    return platforms.length > 0 ? platforms : ['pdf']; // Default to pdf if none specified
  };

  // Filter e-books based on search only
  const filteredEbooks = useMemo(() => {
    return ebooks.filter(book => {
      if (searchQuery && !book.title.toLowerCase().includes(searchQuery.toLowerCase()) && 
          !book.author.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [searchQuery, ebooks]);

  const handleViewDetails = useCallback((book) => {
    setSelectedBook(book);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedBook(null);
    setShowPdfViewer(false);
    setDownloadProgress(0);
  }, []);

  const simulateDownload = useCallback(() => {
    setDownloadProgress(0);
    const interval = setInterval(() => {
      setDownloadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            window.open(selectedBook.pdfUrl, '_blank');
            setDownloadProgress(0);
          }, 500);
          return 100;
        }
        return prev + 5;
      });
    }, 50);
  }, [selectedBook]);

  const handleDownload = useCallback((book) => {
    simulateDownload();
  }, [simulateDownload]);

  const handleOpenPlayStore = useCallback((book) => {
    window.open(book.playstoreLink, '_blank');
  }, []);

  const handleOpenWeb = useCallback((book) => {
    window.open(book.webLink, '_blank');
  }, []);

  const handleViewPdf = useCallback(() => {
    setShowPdfViewer(true);
  }, []);

  const toggleSaveBook = useCallback((bookId) => {
    setSavedBooks(prev => 
      prev.includes(bookId) 
        ? prev.filter(id => id !== bookId)
        : [...prev, bookId]
    );
  }, []);

  const handleShare = useCallback((book) => {
    if (navigator.share) {
      navigator.share({
        title: book.title,
        text: book.description,
        url: book.webLink,
      });
    } else {
      alert(`Sharing "${book.title}"...`);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Download Progress Overlay */}
      <AnimatePresence>
        {downloadProgress > 0 && downloadProgress < 100 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-2xl p-8 text-center max-w-md w-full mx-4"
            >
              <FaCloudDownloadAlt className="text-6xl text-blue-500 mx-auto mb-4 animate-bounce" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Downloading E-Book</h3>
              <p className="text-gray-600 mb-6">{selectedBook?.title}</p>
              
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <motion.div
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${downloadProgress}%` }}
                />
              </div>
              <div className="text-sm text-gray-600">{downloadProgress}% complete</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative min-h-[50vh] lg:min-h-[60vh] overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-500 to-indigo-400"
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-4 h-4 bg-white/20 rounded-full"
              initial={{ y: -100, x: Math.random() * 100 }}
              animate={{ 
                y: 800,
                x: Math.random() * 100 - 50,
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5
              }}
              style={{
                left: `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>

        <div className="relative h-full flex items-center py-8 lg:py-12 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center mb-6 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <div className="mr-3">
                  <GiOpenBook className="text-white text-xl" />
                </div>
                <span className="font-semibold text-white">Digital Health Library</span>
              </div>

              <h1 className="text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Health <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">E-Books</span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-white/90 mb-8 max-w-3xl mx-auto lg:mx-0">
                Premium health education resources available on Play Store & Downloadable PDFs
              </p>

              {/* Search Bar */}
              <div className="max-w-2xl">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search e-books by title, author, or topic..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-6 py-4 pl-12 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                  />
                  <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-lg" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Platform Badges */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center space-x-6">
          {platforms.map((platform) => (
            <motion.div
              key={platform.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: platform.id === 'playstore' ? 0.2 : platform.id === 'web' ? 0.4 : platform.id === 'pdf' ? 0.6 : 0.8 }}
              className="flex flex-col items-center"
            >
              <div className={`bg-gradient-to-r ${platform.color} w-10 h-10 rounded-xl flex items-center justify-center mb-2`}>
                <div className="text-white text-lg">
                  {platform.icon}
                </div>
              </div>
              <span className="text-white text-xs font-medium">{platform.label}</span>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Main Content */}
      <div className="py-8 lg:py-12 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            {!selectedBook ? (
              /* E-Books List View */
              <motion.div
                key="list"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={fadeInUp}
              >
                {/* Header */}
                <motion.div
                  variants={fadeInUp}
                  className="mb-8 lg:mb-12"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 lg:mb-8">
                    <div>
                      <h2 className="text-2xl lg:text-3xl font-bold mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                        Explore Health E-Books
                      </h2>
                      <p className="text-gray-600">
                        Available in multiple formats for your convenience
                      </p>
                    </div>
                    <div className="mt-4 lg:mt-0 text-sm text-gray-600">
                      <span className="font-bold text-indigo-600">{filteredEbooks.length}</span> books found
                    </div>
                  </div>
                </motion.div>

                {/* E-Books Grid */}
                <motion.div
                  variants={staggerContainer}
                  className="mb-12"
                >
                  {loading ? (
                    <motion.div 
                      variants={fadeInUp}
                      className="text-center py-16 lg:py-24 bg-white rounded-2xl border border-gray-200"
                    >
                      <FaSpinner className="text-6xl text-indigo-500 mx-auto mb-4 animate-spin" />
                      <h3 className="text-xl lg:text-2xl font-bold text-gray-700 mb-3">Loading E-Books...</h3>
                      <p className="text-gray-500 max-w-md mx-auto">
                        Please wait while we fetch the latest health e-books
                      </p>
                    </motion.div>
                  ) : error ? (
                    <motion.div 
                      variants={fadeInUp}
                      className="text-center py-16 lg:py-24 bg-red-50 rounded-2xl border border-red-200"
                    >
                      <FaTimesCircle className="text-6xl text-red-500 mx-auto mb-4" />
                      <h3 className="text-xl lg:text-2xl font-bold text-red-700 mb-3">Error Loading E-Books</h3>
                      <p className="text-red-600 max-w-md mx-auto mb-4">
                        {error}
                      </p>
                      <button 
                        onClick={() => window.location.reload()}
                        className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all"
                      >
                        Retry
                      </button>
                    </motion.div>
                  ) : filteredEbooks.length === 0 ? (
                    <motion.div 
                      variants={fadeInUp}
                      className="text-center py-16 lg:py-24 bg-white rounded-2xl border border-gray-200"
                    >
                      <GiBookshelf className="text-6xl text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl lg:text-2xl font-bold text-gray-700 mb-3">No E-Books Found</h3>
                      <p className="text-gray-500 max-w-md mx-auto">
                        Try adjusting your search or filter criteria
                      </p>
                    </motion.div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                      {filteredEbooks.map((book, index) => (
                        <motion.div
                          key={book.id}
                          variants={scaleIn}
                          initial={{ opacity: 0, y: 50 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          whileHover={{ 
                            y: -8,
                            transition: { duration: 0.3 }
                          }}
                          className={`${book.bgColor} border ${book.borderColor} rounded-2xl lg:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group`}
                        >
                          {/* Book Image */}
                          <div className="relative h-48 lg:h-56 overflow-hidden">
                            <img 
                              src={book.image} 
                              alt={book.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                            
                            {/* Badges */}
                            <div className="absolute top-4 left-4 space-y-2">
                              {book.isNew && (
                                <motion.div 
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-lg">
                                  NEW
                                </motion.div>
                              )}
                              {book.isFree ? (
                                <motion.div 
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-lg">
                                  FREE
                                </motion.div>
                              ) : (
                                <motion.div 
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-lg">
                                  {book.discountPercent}% OFF
                                </motion.div>
                              )}
                            </div>
                            
                            {/* Platform Badges */}
                            <div className="absolute top-4 right-4 flex flex-col gap-2">
                              {book.platforms.includes('playstore') && (
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPlayStore(book);
                                  }}
                                  className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-2 rounded-lg shadow-lg hover:shadow-xl"
                                  title="Available on Play Store"
                                >
                                  <FaAndroid className="text-sm" />
                                </motion.button>
                              )}
                              {book.platforms.includes('pdf') && (
                                <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-2 rounded-lg shadow-lg">
                                  <FaFilePdf className="text-sm" />
                                </div>
                              )}
                            </div>
                            
                            {/* Save Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSaveBook(book.id);
                              }}
                              className="absolute bottom-4 right-4 bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/30 transition-colors"
                            >
                              {savedBooks.includes(book.id) ? (
                                <FaBookmark className="text-white" />
                              ) : (
                                <FaBookmark className="text-white/70" />
                              )}
                            </button>
                          </div>

                          {/* Book Info */}
                          <div className="p-4 lg:p-6">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="font-bold text-gray-800 text-lg lg:text-xl mb-1 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                                  {book.title}
                                </h3>
                                <p className="text-gray-600 text-sm">by {book.author}</p>
                              </div>
                              <div className="text-right">
                                {book.isFree ? (
                                  <span className="text-green-600 font-bold text-lg">FREE</span>
                                ) : (
                                  <div>
                                    <span className="font-bold text-gray-800 text-lg">₹{book.discountPrice}</span>
                                    <div className="text-gray-500 line-through text-sm">₹{book.price}</div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Rating */}
                            <div className="flex items-center mb-4">
                              <div className="flex mr-2">
                                {[...Array(5)].map((_, i) => (
                                  <FaStar
                                    key={i}
                                    className={`${i < Math.floor(book.rating) ? 'text-yellow-400' : 'text-gray-300'} mr-1`}
                                    size={14}
                                  />
                                ))}
                              </div>
                              <span className="text-gray-700 font-bold">{book.rating}/5</span>
                              <span className="text-gray-500 text-sm ml-2">({book.reviews})</span>
                            </div>

                            {/* Description */}
                            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{book.description}</p>

                            {/* Stats */}
                            <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                              <div className="flex items-center">
                                <FaFileAlt className="mr-2" />
                                <span>{book.format}</span>
                              </div>
                              <div className="flex items-center">
                                <FaClock className="mr-2" />
                                <span>{book.pages} pages</span>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="grid grid-cols-2 gap-3">
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleViewDetails(book)}
                                className="flex items-center justify-center space-x-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all text-sm"
                              >
                                <FaEye />
                                <span>View Details</span>
                              </motion.button>
                              
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPlayStore(book);
                                }}
                                className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all text-sm"
                              >
                                <FaAndroid />
                                <span>Play Store</span>
                              </motion.button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>

                {/* Platform Features */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl lg:rounded-3xl p-6 lg:p-8 text-white shadow-2xl"
                >
                  <div className="text-center mb-8">
                    <h3 className="text-2xl lg:text-3xl font-bold mb-4">Multiple Access Options</h3>
                    <p className="text-white/90">Choose how you want to access our e-books</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {platforms.map((platform) => (
                      <motion.div
                        key={platform.id}
                        whileHover={{ y: -5 }}
                        className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors"
                      >
                        <div className={`inline-flex p-4 rounded-2xl mb-4 bg-gradient-to-r ${platform.color}`}>
                          <div className="text-white text-2xl">
                            {platform.icon}
                          </div>
                        </div>
                        <h4 className="font-bold text-lg mb-2">{platform.label}</h4>
                        <p className="text-white/80 text-sm">
                          {platform.id === 'playstore' && 'Download from Google Play Store'}
                          {platform.id === 'pdf' && 'Download PDF for offline reading'}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            ) : (
              /* E-Book Details View */
              <motion.div
                key="details"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-8"
              >
                {/* Back Button */}
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  whileHover={{ x: -5 }}
                  onClick={handleBackToList}
                  className="flex items-center text-indigo-600 hover:text-indigo-700 font-medium transition-colors group"
                >
                  <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
                  Back to All Books
                </motion.button>

                {/* Book Header */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl lg:rounded-3xl shadow-xl overflow-hidden border border-gray-200"
                >
                  <div className="p-6 lg:p-8">
                    <div className="flex flex-col lg:flex-row gap-8">
                      {/* Book Image */}
                      <div className="lg:w-2/5">
                        <div className="relative rounded-2xl overflow-hidden">
                          <img 
                            src={selectedBook.image} 
                            alt={selectedBook.title}
                            className="w-full h-64 lg:h-80 object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                          
                          {/* Badges */}
                          <div className="absolute top-4 left-4 space-y-2">
                            {selectedBook.isNew && (
                              <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 rounded-lg font-bold shadow-lg">
                                NEW RELEASE
                              </span>
                            )}
                            {selectedBook.isFree ? (
                              <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-2 rounded-lg font-bold shadow-lg">
                                FREE EBOOK
                              </span>
                            ) : (
                              <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-lg font-bold shadow-lg">
                                {selectedBook.discountPercent}% OFF
                              </span>
                            )}
                          </div>
                          
                          {/* Save Button */}
                          <button
                            onClick={() => toggleSaveBook(selectedBook.id)}
                            className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm p-3 rounded-full hover:bg-white/30 transition-colors"
                          >
                            {savedBooks.includes(selectedBook.id) ? (
                              <FaBookmark className="text-white text-xl" />
                            ) : (
                              <FaBookmark className="text-white/70 text-xl" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Book Info */}
                      <div className="lg:w-3/5">
                        <div className="mb-6">
                          <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-2">
                            {selectedBook.title}
                          </h1>
                          <p className="text-lg text-gray-600 mb-4">by <span className="font-medium text-indigo-600">{selectedBook.author}</span></p>
                          
                          <div className="flex items-center mb-4">
                            <div className="flex mr-3">
                              {[...Array(5)].map((_, i) => (
                                <FaStar
                                  key={i}
                                  className={`${i < Math.floor(selectedBook.rating) ? 'text-yellow-400' : 'text-gray-300'} mr-1`}
                                  size={16}
                                />
                              ))}
                            </div>
                            <span className="text-gray-700 font-bold">{selectedBook.rating}/5</span>
                            <span className="text-gray-500 ml-2">({selectedBook.reviews} reviews)</span>
                          </div>

                          <p className="text-gray-700 mb-6">{selectedBook.description}</p>
                        </div>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                          <div className="bg-gray-50 p-4 rounded-xl">
                            <div className="text-gray-600 text-sm mb-1">Format</div>
                            <div className="font-bold text-gray-800">{selectedBook.format}</div>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-xl">
                            <div className="text-gray-600 text-sm mb-1">Pages</div>
                            <div className="font-bold text-gray-800">{selectedBook.pages}</div>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-xl">
                            <div className="text-gray-600 text-sm mb-1">Language</div>
                            <div className="font-bold text-gray-800">{selectedBook.language}</div>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-xl">
                            <div className="text-gray-600 text-sm mb-1">Preview</div>
                            <div className="font-bold text-gray-800">{selectedBook.previewPages} pages</div>
                          </div>
                        </div>

                        {/* Price */}
                        <div className="mb-6">
                          {selectedBook.isFree ? (
                            <div className="text-center bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                              <div className="text-3xl font-bold text-green-600 mb-1">FREE</div>
                              <div className="text-green-700">No payment required</div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-6">
                              <div>
                                <div className="text-3xl font-bold text-gray-800">₹{selectedBook.discountPrice}</div>
                                <div className="text-gray-500 line-through">₹{selectedBook.price}</div>
                              </div>
                              <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-lg font-bold">
                                Save {selectedBook.discountPercent}%
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleOpenPlayStore(selectedBook)}
                            className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-3 rounded-xl hover:shadow-lg transition-all"
                          >
                            <FaAndroid />
                            <span>Play Store</span>
                          </motion.button>
                          
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDownload(selectedBook)}
                            className="flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-3 rounded-xl hover:shadow-lg transition-all"
                          >
                            <FaDownload />
                            <span>Download PDF</span>
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Book Details */}
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
                  {/* Chapters */}
                  <div className="lg:col-span-3">
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                      <h2 className="text-xl font-bold text-gray-800 mb-6">Table of Contents</h2>
                      <div className="space-y-4">
                        {selectedBook.details.chapters.map((chapter, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                          >
                            <div className="flex items-center">
                              <div className="bg-indigo-100 text-indigo-600 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm mr-4">
                                {index + 1}
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-800">{chapter.title}</h4>
                                <div className="text-sm text-gray-500 mt-1">
                                  {chapter.pages} pages • {chapter.duration}
                                </div>
                              </div>
                            </div>
                            <FaArrowRight className="text-gray-400 group-hover:text-indigo-600 transition-colors" />
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview Section */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 lg:p-8 text-white"
                >
                  <div className="flex flex-col lg:flex-row items-center justify-between">
                    <div className="mb-6 lg:mb-0 lg:mr-8">
                      <h3 className="text-2xl font-bold mb-3">Want to Preview?</h3>
                      <p className="text-white/90 mb-4">
                        Read the first {selectedBook.previewPages} pages for free before deciding
                      </p>
                      <div className="flex items-center">
                        <FaBookOpen className="text-2xl mr-3" />
                        <div>
                          <div className="font-bold">Free Preview Available</div>
                          <div className="text-sm opacity-80">{selectedBook.previewPages} pages preview</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => setShowPdfViewer(true)}
                        className="bg-white text-indigo-600 px-6 py-3 rounded-xl font-bold hover:shadow-2xl transition-all shadow-lg"
                      >
                        <FaEye className="inline mr-3" />
                        View Preview
                      </button>
                      {!selectedBook.isFree && (
                        <button
                          onClick={() => handleOpenPlayStore(selectedBook)}
                          className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-bold hover:shadow-2xl transition-all shadow-lg"
                        >
                          <FaAndroid className="inline mr-3" />
                          Get Full Version
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* PDF Viewer Modal */}
                <AnimatePresence>
                  {showPdfViewer && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    >
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        className="bg-white rounded-2xl lg:rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
                      >
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-gray-800">PDF Preview</h3>
                            <button
                              onClick={() => setShowPdfViewer(false)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              Close
                            </button>
                          </div>
                          <div className="bg-gray-100 rounded-xl p-8 text-center">
                            <FaFilePdf className="text-6xl text-red-500 mx-auto mb-4" />
                            <h4 className="text-xl font-bold text-gray-800 mb-2">{selectedBook.title}</h4>
                            <p className="text-gray-600 mb-6">
                              Preview of {selectedBook.previewPages} pages available
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                              <button
                                onClick={() => window.open(selectedBook.pdfUrl, '_blank')}
                                className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all"
                              >
                                Open Full Preview
                              </button>
                              <button
                                onClick={() => handleDownload(selectedBook)}
                                className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all"
                              >
                                Download Full PDF
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default Ebook;